import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { http } from '../../../api/http';

interface ArticuloResult {
  idArticulo: number;
  descripcion: string;
  codigoArticulo?: string;
  codigoBarra?: string;
  unidadDeMedida?: string;
  grupoArticuloNombre?: string;
  marcaNombre?: string;
  isServicio: boolean;
  manejaStock: string;
  precioVenta?: number;
}

export const useProductSearch = () => {
  const [query, setQueryState] = useState('');
  const [allArticulos, setAllArticulos] = useState<ArticuloResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const lastKeystrokeRef = useRef<number>(0);

  // Load all articles once on mount
  useEffect(() => {
    const loadArticulos = async () => {
      setLoading(true);
      try {
        const response = await http.get('/pos/articulos');
        setAllArticulos(response.data);
        setInitialLoaded(true);
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setLoading(false);
      }
    };
    loadArticulos();
  }, []);

  // Extract unique categories
  const categories = useMemo(() => {
    const cats = new Set<string>();
    allArticulos.forEach((a) => {
      if (a.grupoArticuloNombre) cats.add(a.grupoArticuloNombre);
    });
    return Array.from(cats).sort();
  }, [allArticulos]);

  // Filter results by query and category
  const results = useMemo(() => {
    let filtered = allArticulos;

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter((a) => a.grupoArticuloNombre === selectedCategory);
    }

    // Filter by search query
    if (query && query.length >= 2) {
      const q = query.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.descripcion?.toLowerCase().includes(q) ||
          a.codigoArticulo?.toLowerCase().includes(q) ||
          a.codigoBarra?.toLowerCase().includes(q)
      );
    }

    // If no category and no query, show nothing (too many items)
    if (!selectedCategory && (!query || query.length < 2)) {
      return [];
    }

    return filtered.slice(0, 50);
  }, [allArticulos, query, selectedCategory]);

  const handleQueryChange = useCallback((value: string) => {
    setQueryState(value);
  }, []);

  const clearSearch = useCallback(() => {
    setQueryState('');
    setSelectedCategory(null);
  }, []);

  return {
    query,
    results,
    loading: loading && !initialLoaded,
    categories,
    selectedCategory,
    setSelectedCategory,
    setQuery: handleQueryChange,
    clearSearch,
  };
};
